// ============================================================================
// DIRECT GRAPHQL HOOKS - USING GENERATED OPERATIONS FOR TIME ENTRIES
// ============================================================================
//
// Following the same pattern as useTicketHooks and useTaskHooks.

import { useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import {
  CreateTimeEntriesDocument,
  CreateTimeEntriesMutation,
  DeleteTimeEntriesDocument,
  DeleteTimeEntriesMutation,
  GetClientTimeEntriesDocument,
  GetClientTimeEntriesQuery,
  GetClientTimeEntriessDocument,
  GetClientTimeEntriessQuery,
  GetTimeEntriesDocument,
  GetTimeEntriesQuery,
  GetTimeEntriessDocument,
  GetTimeEntriessQuery,
  Time_Entries,
  Time_EntriesInsertInput,
  Time_EntriesUpdateInput,
  UpdateTimeEntriesDocument,
  UpdateTimeEntriesMutation,
} from "@/src/gql/graphql";
import { useClientRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import {
  stripGraphqlMeta,
  TimeEntryExtraHooks,
  useBulkApproveTimeEntries,
  useBulkDeleteTimeEntries,
  useBulkRejectTimeEntries,
  useUpdateTimeEntrySyncStatus,
  withTimeEntryNodeId,
} from "./time-entry-hook-helpers";
import {
  useAutoGenerateTicketId,
  useCreateTimeEntryTemplate,
  useDeleteTimeEntryTemplate,
  useTimeEntryTemplates,
  useToggleTimeEntryTemplateActive,
  useUpdateTimeEntryTemplate,
} from "./time-entry-template-hooks";
import { serializeGraphqlInput } from "@/lib/graphql/serialize-input";
import { usePaginatedCollection } from "@/lib/hooks/api/shared/use-paginated-collection";

const SLACK_WEBHOOK_PATH = "/api/webhook/slack";

async function notifySlackForTimeEntry(timeEntry: unknown): Promise<void> {
  try {
    await fetch(SLACK_WEBHOOK_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(timeEntry),
    });
  } catch (webhookError) {
    console.error("Failed to send Slack notification:", webhookError);
  }
}

function usePaginatedTimeEntries(params?: Record<string, unknown>) {
  // Canonical list-query path (cache-and-network) — see use-paginated-collection.ts.
  //
  // SECURITY: client_user reads a column-restricted query that selects ONLY the
  // work-transparency columns (CLIENT_USER_ACCESS_SCOPE). Internal columns
  // (billable_rate, quickbooks_*, sync_status, approval fields, user_id, raw
  // times, …) are never selected for a client, so they never reach the wire.
  // RLS already row-scopes which entries a client can read.
  const { isClientUser } = useClientRoleCheck();
  return usePaginatedCollection(
    isClientUser ? GetClientTimeEntriessDocument : GetTimeEntriessDocument,
    params,
    (data: GetTimeEntriessQuery | GetClientTimeEntriessQuery) =>
      data.time_entriesCollection,
    (node) => withTimeEntryNodeId(node) as Time_Entries
  );
}

function useInfiniteTimeEntries(_params?: Record<string, unknown>) {
  // TODO: Implement infinite query
  return {
    data: undefined,
    isLoading: false,
    error: null,
    refetch: () => {},
    hasNextPage: false,
    isFetchingNextPage: false,
    fetchNextPage: () => {},
  };
}

function useSingleTimeEntry(id: string, options?: { enabled?: boolean }) {
  // SECURITY: client_user reads the column-restricted single query (safe columns
  // only). See usePaginatedTimeEntries / CLIENT_USER_ACCESS_SCOPE.
  const { isClientUser } = useClientRoleCheck();
  const result = useQuery<GetTimeEntriesQuery | GetClientTimeEntriesQuery>(
    isClientUser ? GetClientTimeEntriesDocument : GetTimeEntriesDocument,
    {
      variables: { id },
      errorPolicy: "all",
      skip: !id || (options && options.enabled === false),
    }
  );

  return {
    ...result,
    data: result.data?.time_entriesCollection?.edges?.[0]?.node
      ? (withTimeEntryNodeId(
          result.data.time_entriesCollection.edges[0].node
        ) as Time_Entries)
      : undefined,
    isLoading: result.loading,
    error: result.error || null,
    refetch: result.refetch,
  };
}

function useUpdateTimeEntry() {
  const [mutate, result] = useMutation<UpdateTimeEntriesMutation>(UpdateTimeEntriesDocument);
  return {
    mutateAsync: async ({
      id,
      ...input
    }: { id: string } & Time_EntriesUpdateInput) => {
      const cleanInput = serializeGraphqlInput(
        "time_entries",
        stripGraphqlMeta(input as Time_EntriesUpdateInput)
      );

      const { data, error } = await mutate({
        variables: { id, input: cleanInput },
        errorPolicy: "all",
      });
      if (error) throw new Error(`GraphQL error: ${error.message}`);
      const updated = data?.updatetime_entriesCollection?.records?.[0];
      if (!updated) throw new Error("Failed to update time entry");
      return withTimeEntryNodeId(updated) as Time_Entries;
    },
    isPending: result.loading,
  };
}

function useCreateTimeEntry() {
  const [mutate, result] = useMutation<CreateTimeEntriesMutation>(CreateTimeEntriesDocument);
  return {
    mutateAsync: async (input: Time_EntriesInsertInput) => {
      const { __typename: _typename, id: _id, ...rest } =
        input as Time_EntriesInsertInput & { __typename?: unknown };
      const cleanInput = serializeGraphqlInput("time_entries", rest);

      const { data, error } = await mutate({
        variables: { input: cleanInput },
        errorPolicy: "all",
        refetchQueries: ["GetTimeEntriess"],
        awaitRefetchQueries: true,
      });
      if (error) throw new Error(`GraphQL error: ${error.message}`);
      const created = data?.insertIntotime_entriesCollection?.records?.[0];
      if (!created) throw new Error("Failed to create time entry");

      // Send Slack notification if time entry was created successfully
      if (created) {
        await notifySlackForTimeEntry(created);
      }

      return withTimeEntryNodeId(created) as Time_Entries;
    },
    isPending: result.loading,
  };
}

function useDeleteTimeEntry() {
  const [mutate, result] = useMutation<DeleteTimeEntriesMutation>(DeleteTimeEntriesDocument);
  return {
    mutateAsync: async (id: string) => {
      const { data, error } = await mutate({
        variables: { id },
        errorPolicy: "all",
        refetchQueries: ["GetTimeEntriess"],
        awaitRefetchQueries: true,
      });
      if (error) throw new Error(`GraphQL error: ${error.message}`);
      const deletedId = data?.deleteFromtime_entriesCollection?.records?.[0]?.id;
      return deletedId || id;
    },
    isPending: result.loading,
  };
}

export const useTimeEntryHooks = (): EntityHooks<Time_Entries> & TimeEntryExtraHooks => {
  return useMemo(
    (): EntityHooks<Time_Entries> & TimeEntryExtraHooks => ({
      queryDocument: GetTimeEntriessDocument,
      usePaginated: usePaginatedTimeEntries,
      useInfinite: useInfiniteTimeEntries,
      useSingle: useSingleTimeEntry,
      useUpdate: useUpdateTimeEntry,
      useCreate: useCreateTimeEntry,
      useDelete: useDeleteTimeEntry,

      // Bulk operations
      useBulkApprove: useBulkApproveTimeEntries,
      useBulkReject: useBulkRejectTimeEntries,
      useBulkDelete: useBulkDeleteTimeEntries,
      useUpdateSyncStatus: useUpdateTimeEntrySyncStatus,

      // Template operations - placeholders for future implementation
      useTemplates: useTimeEntryTemplates,
      useCreateTemplate: useCreateTimeEntryTemplate,
      useUpdateTemplate: useUpdateTimeEntryTemplate,
      useDeleteTemplate: useDeleteTimeEntryTemplate,
      useToggleTemplateActive: useToggleTimeEntryTemplateActive,

      // Auto-generate ticket ID based on description title
      useAutoGenerateTicketId,
    }),
    []
  );
};
